import { useMemo } from 'react';
import { type SelectionMap, type SelectionState } from '../components/FilterSelector/types';

interface SelectionAnalysis {
  allAdd: boolean;
  allRemove: boolean;
  selectedCount: number;
  excludedCount: number;
  totalCount: number;
}

/**
 * Analyzes a selection map to determine if all items are selected or excluded.
 * 
 * @param selection - The selection map to analyze
 * @param availableItemsCount - The total number of available items
 * @returns Analysis object with allAdd, allRemove, and counts
 */
export function useSelectionAnalysis(
  selection: SelectionMap,
  availableItemsCount: number,
): SelectionAnalysis {
  return useMemo(() => {
    const selectedCount = Object.values(selection).filter((v) => v === 'add').length;
    const excludedCount = Object.values(selection).filter((v) => v === 'remove').length;
    const totalCount = selectedCount + excludedCount;
    
    const allAdd = availableItemsCount > 0 && selectedCount === availableItemsCount && excludedCount === 0;
    const allRemove = availableItemsCount > 0 && excludedCount === availableItemsCount && selectedCount === 0;

    return {
      allAdd,
      allRemove,
      selectedCount,
      excludedCount,
      totalCount,
    };
  }, [selection, availableItemsCount]);
}

/**
 * Checks if all items in the list have a specific selection state.
 * 
 * @param itemKeys - Array of item keys to check
 * @param selection - The selection map
 * @param state - The state to check for ('add' or 'remove')
 * @returns true if all items have the specified state
 */
export function checkAllSelected(
  itemKeys: string[],
  selection: SelectionMap,
  state: SelectionState,
): boolean {
  return itemKeys.length > 0 && itemKeys.every((key) => selection[key] === state);
}
